import { reactive } from 'vue';
import type { NavigationGuard } from 'vue-router';
import { env } from '../config/env';
import type { ModuleId } from '../module-api';
import { postLogin, postLogout, postRefresh, postTokenExchange, AuthApiError } from './authApi';
import {
  accessStorageKey,
  API_AUDIENCES,
  audienceAppName,
  LOGIN_AUDIENCES,
  MENU_AUDIENCES,
  type ApiAudience,
  type MenuAudience,
} from './audiences';

export type { ApiAudience, MenuAudience } from './audiences';
export {
  API_AUDIENCES,
  LOGIN_AUDIENCES,
  MENU_AUDIENCES,
  accessStorageKey,
  audienceAppName,
} from './audiences';
export type { AuthUser } from './usersApi';
export { fetchAllAuthUsers, fetchAuthUsersPage } from './usersApi';
export { getAuthUsersLookup } from './usersLookup';

const REFRESH_KEY = 'botto_admin_refresh';
const USER_KEY = 'botto_admin_user_name';

/** Refresh this many seconds before JWT `exp`. */
const EXPIRY_SKEW_SEC = 30;

export interface LoginCredentials {
  /** User email (auth API field `email`). */
  login: string;
  password: string;
}

/** Reactive module visibility for side-nav / home (JWT permissions per audience). */
export const moduleAccessState = reactive({
  /** Module ids the current user may open. */
  allowed: [] as ModuleId[],
  /** True after the latest `refreshModuleAccess` finished. */
  ready: false,
});

export interface AuthService {
  isAuthenticated(): boolean;
  getAccessToken(audience: ApiAudience): string | null;
  getRefreshToken(): string | null;
  getDisplayName(): string | null;
  login(credentials: LoginCredentials): Promise<void>;
  logout(): Promise<void>;
  /**
   * Ensure a non-expired access token for the given API audience (via /auth/token).
   * @param force when true, always re-exchange (e.g. after API 401)
   */
  ensureAccessToken(audience: ApiAudience, force?: boolean): Promise<boolean>;
  /** Probe each menu audience and update `moduleAccessState`. */
  refreshModuleAccess(): Promise<ModuleId[]>;
  canAccessModule(moduleId: ModuleId): boolean;
}

function readStorage(key: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(key);
}

function writeRefresh(refresh_token: string): void {
  localStorage.setItem(REFRESH_KEY, refresh_token);
}

function writeAccess(audience: ApiAudience, access_token: string): void {
  localStorage.setItem(accessStorageKey(audience), access_token);
}

function clearAccessTokens(): void {
  for (const aud of API_AUDIENCES) {
    localStorage.removeItem(accessStorageKey(aud));
  }
}

function clearSession(): void {
  clearAccessTokens();
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  moduleAccessState.allowed = [];
  moduleAccessState.ready = false;
}

interface JwtPayload {
  exp?: number;
  aud?: string | string[];
  permissions?: string[];
}

/** Decode JWT payload without verifying signature (client-side expiry / aud check only). */
function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function jwtAudMatches(aud: unknown, expected: string): boolean {
  if (typeof aud === 'string') return aud === expected;
  if (Array.isArray(aud)) return aud.includes(expected);
  return false;
}

function isJwtUsable(token: string | null, expectedAud?: string): boolean {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  if (expectedAud && payload.aud != null && !jwtAudMatches(payload.aud, expectedAud)) return false;
  const exp = payload.exp;
  if (exp == null) return true;
  return exp * 1000 > Date.now() + EXPIRY_SKEW_SEC * 1000;
}

/** Access token is usable and carries at least one permission for that API. */
function tokenGrantsModuleAccess(token: string | null, expectedAud: string): boolean {
  if (!isJwtUsable(token, expectedAud)) return false;
  const payload = decodeJwtPayload(token!);
  const perms = payload?.permissions;
  return Array.isArray(perms) && perms.length > 0;
}

class AuthServiceImpl implements AuthService {
  private refreshInFlight: Promise<boolean> | null = null;
  private readonly exchangeInFlight = new Map<ApiAudience, Promise<boolean>>();
  private accessRefreshInFlight: Promise<ModuleId[]> | null = null;

  isAuthenticated(): boolean {
    if (!env.authEnabled) return true;
    return !!this.getRefreshToken();
  }

  getAccessToken(audience: ApiAudience): string | null {
    return readStorage(accessStorageKey(audience));
  }

  getRefreshToken(): string | null {
    return readStorage(REFRESH_KEY);
  }

  getDisplayName(): string | null {
    return readStorage(USER_KEY);
  }

  canAccessModule(moduleId: ModuleId): boolean {
    if (!env.authEnabled) return true;
    return moduleAccessState.allowed.includes(moduleId);
  }

  async refreshModuleAccess(): Promise<ModuleId[]> {
    if (!env.authEnabled) {
      moduleAccessState.allowed = [...MENU_AUDIENCES];
      moduleAccessState.ready = true;
      return moduleAccessState.allowed;
    }

    if (this.accessRefreshInFlight) return this.accessRefreshInFlight;

    this.accessRefreshInFlight = (async () => {
      const allowed: ModuleId[] = [];
      await Promise.all(
        MENU_AUDIENCES.map(async (aud) => {
          const ok = await this.ensureAccessToken(aud);
          if (!ok) return;
          const appName = audienceAppName(aud);
          if (tokenGrantsModuleAccess(this.getAccessToken(aud), appName)) {
            allowed.push(aud);
          }
        }),
      );
      // Keep MODULE_ORDER (MENU_AUDIENCES order).
      moduleAccessState.allowed = MENU_AUDIENCES.filter((id) => allowed.includes(id));
      moduleAccessState.ready = true;
      return moduleAccessState.allowed;
    })();

    try {
      return await this.accessRefreshInFlight;
    } finally {
      this.accessRefreshInFlight = null;
    }
  }

  async login(credentials: LoginCredentials): Promise<void> {
    const email = credentials.login.trim();
    if (!email || !credentials.password) {
      throw new Error('Укажите логин и пароль');
    }
    if (!env.authAppName) {
      throw new Error('Не задан VITE_AUTH_APP_NAME в .env');
    }

    const pair = await postLogin({
      email,
      password: credentials.password,
      app_name: env.authAppName,
    });

    clearSession();
    writeRefresh(pair.refresh_token);
    localStorage.setItem(USER_KEY, email);

    await Promise.all(LOGIN_AUDIENCES.map((aud) => this.ensureAccessToken(aud)));
    const allowed = await this.refreshModuleAccess();
    if (allowed.length === 0) {
      clearSession();
      throw new Error('Нет доступа ни к одному разделу админки');
    }
  }

  async logout(): Promise<void> {
    const refresh = this.getRefreshToken();
    try {
      if (refresh) await postLogout(refresh);
    } catch {
      /* clear locally anyway */
    }
    clearSession();
    this.refreshInFlight = null;
    this.exchangeInFlight.clear();
    this.accessRefreshInFlight = null;
  }

  async ensureAccessToken(audience: ApiAudience, force = false): Promise<boolean> {
    if (!env.authEnabled) return true;

    const appName = audienceAppName(audience);
    const cached = this.getAccessToken(audience);
    if (!force && isJwtUsable(cached, appName)) return true;

    const inFlight = this.exchangeInFlight.get(audience);
    if (inFlight) return inFlight;

    const promise = this.exchangeForAudience(audience);
    this.exchangeInFlight.set(audience, promise);
    try {
      return await promise;
    } finally {
      this.exchangeInFlight.delete(audience);
    }
  }

  private async exchangeForAudience(audience: ApiAudience): Promise<boolean> {
    const appName = audienceAppName(audience);

    // Same auth app as SMS → reuse existing sms access token (no extra /auth/token call).
    if (audience !== 'sms' && appName === audienceAppName('sms')) {
      const smsToken = this.getAccessToken('sms');
      if (isJwtUsable(smsToken, appName)) {
        writeAccess(audience, smsToken!);
        return true;
      }
    }

    const ok = await this.ensureRefreshToken();
    if (!ok) return false;

    const refresh = this.getRefreshToken();
    if (!refresh) {
      clearSession();
      return false;
    }

    try {
      const { access_token } = await postTokenExchange(refresh, appName);
      writeAccess(audience, access_token);
      return true;
    } catch (e) {
      // Unknown app_name in auth service — do not rotate refresh or wipe session.
      if (e instanceof AuthApiError && e.status === 404) {
        console.warn(`[auth] app "${appName}" not found (${audience} audience)`);
        return false;
      }

      const rotated = await this.rotateRefresh();
      if (!rotated) return false;
      try {
        const nextRefresh = this.getRefreshToken();
        if (!nextRefresh) return false;
        const { access_token } = await postTokenExchange(nextRefresh, appName);
        writeAccess(audience, access_token);
        return true;
      } catch (retryErr) {
        if (retryErr instanceof AuthApiError && retryErr.status === 404) {
          console.warn(`[auth] app "${appName}" not found (${audience} audience)`);
          return false;
        }
        clearSession();
        return false;
      }
    }
  }

  /** Rotate portal refresh when near expiry (/auth/refresh). */
  private async ensureRefreshToken(): Promise<boolean> {
    const refresh = this.getRefreshToken();
    if (!refresh) return false;
    if (isJwtUsable(refresh)) return true;
    return this.rotateRefresh();
  }

  private rotateRefresh(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight;

    this.refreshInFlight = (async () => {
      const refresh = this.getRefreshToken();
      if (!refresh) {
        clearSession();
        return false;
      }
      try {
        const pair = await postRefresh(refresh);
        writeRefresh(pair.refresh_token);
        clearAccessTokens();
        return true;
      } catch {
        clearSession();
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }
}

export const authService: AuthService = new AuthServiceImpl();

/** True if the access token for ``audience`` includes ``permission`` or ``admin:all``. */
export function jwtHasPermission(audience: ApiAudience, permission: string): boolean {
  if (!env.authEnabled) return true;
  const token = authService.getAccessToken(audience);
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  const perms = payload?.permissions;
  if (!Array.isArray(perms)) return false;
  // Empty permissions historically meant full access for some HS256 tokens.
  if (perms.length === 0) return true;
  const list = perms.map(String);
  return list.includes(permission) || list.includes('admin:all');
}

type LoginRedirect = (redirectPath: string) => void | Promise<void>;
let loginRedirect: LoginRedirect | null = null;
let redirectingToLogin = false;

/** Shell registers SPA navigation so API 401 can send the user to `/login`. */
export function configureAuthRedirect(fn: LoginRedirect): void {
  loginRedirect = fn;
}

export async function redirectToLogin(): Promise<void> {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  try {
    await authService.logout();
    const redirect =
      typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
    if (redirect.startsWith('/login')) return;
    if (loginRedirect) {
      await loginRedirect(redirect);
      return;
    }
    if (typeof window !== 'undefined') {
      window.location.assign(`/login?redirect=${encodeURIComponent(redirect)}`);
    }
  } finally {
    redirectingToLogin = false;
  }
}

function firstAllowedHomePath(): string {
  const first = moduleAccessState.allowed[0];
  return first ? `/${first}` : '/';
}

export const authGuard: NavigationGuard = async (to) => {
  if (to.meta?.public) return true;

  if (!env.authEnabled) {
    await authService.refreshModuleAccess();
    return true;
  }

  if (!authService.isAuthenticated()) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  const allowed = await authService.refreshModuleAccess();
  if (allowed.length === 0) {
    await authService.logout();
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  const moduleId = to.meta?.moduleId as ModuleId | undefined;
  if (moduleId && !authService.canAccessModule(moduleId)) {
    return { path: firstAllowedHomePath() };
  }

  const needPerm = to.meta?.requiresPermission as string | undefined;
  if (needPerm && moduleId) {
    await authService.ensureAccessToken(moduleId);
    if (!jwtHasPermission(moduleId, needPerm)) {
      return { path: `/${moduleId}` };
    }
  }

  return true;
};
